"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.Slide = exports.Rail = void 0;
require("./Rail.css");
require("swiper/swiper-bundle.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _nanoid = require("nanoid");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _modules = require("swiper/modules");
var _react2 = require("swiper/react");
var _deviceDetection = require("../../../src/util/inflectionUtils/deviceDetection/deviceDetection");
var _handleResize = require("../../../src/util/inflectionUtils/handleResize/handleResize");
var _carouselUtils = require("../../util/carouselUtils/carouselUtils");
var _renderObject = require("../../util/renderObject/renderObject");
var _TitlesDefault = _interopRequireDefault(require("../TitlesDefault/TitlesDefault"));
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
// eslint-disable-line
// eslint-disable-line

const {
  BREAKPOINTS
} = require('../../util/constants/breakpoints').default;

/**
 * Rail component
 *
 * @method
 * @param { RailProps } props
 * @returns Rail
 */

const Rail = props => {
  const {
    /* Array of items going into Rail */
    children,
    items,
    /* Actions coming from web-core */
    externalActionElementBackButton,
    // rail back button
    externalActionElementForwardButton,
    // rail forward button
    /* ProductCard coming from web-core */
    externalElementMobileSpotlightCard,
    /* TitlesDefault Props */
    externalActionElementLink,
    // Headline link
    headline,
    headlineTag,
    subheadline,
    /* Slider Props */
    carouselRef,
    centeredSlides = defaultProps.centeredSlides,
    id,
    // amplience component ID for the previous/next buttons
    isHighlighter,
    mobilePeek = defaultProps.mobilePeek,
    showBullets = defaultProps.showBullets,
    showCountLabel = defaultProps.showCountLabel,
    slidesOffsetAfter,
    slidesOffsetBefore,
    slidesPerViewAuto = defaultProps.slidesPerViewAuto,
    sliderPerViewXL,
    sliderPerViewLG,
    sliderHighlightPerViewLG,
    sliderPerViewMD,
    sliderPerViewSM,
    slidesPerGroupXL,
    slidesPerGroupLG,
    slidesHighlightPerGroupLG,
    slidesPerGroupMD,
    slidesPerGroupSM,
    spaceBetween = defaultProps.spaceBetween,
    tabletPeek = defaultProps.tabletPeek,
    useFreeMode = defaultProps.useFreeMode,
    useObserver = defaultProps.useObserver,
    useObserveSlideChildren = defaultProps.observeSlideChildren,
    _railADAmeta,
    _railMeta
  } = props;

  /**
   * Getting the current breakpoint
   */
  const [currentBreakpoint, setCurrentBreakpoint] = (0, _react.useState)((0, _deviceDetection.getCurrentBreakpoint)());
  const handleResizeCallback = inflection => {
    const {
      breakpoint
    } = inflection;
    setCurrentBreakpoint(breakpoint);
  };
  (0, _react.useEffect)(() => {
    const removeResizeListener = (0, _handleResize.handleResize)(handleResizeCallback);
    return () => {
      removeResizeListener();
    };
  });

  /**
   * ADA Accessibility
   */
  const {
    carouselAccessibility,
    carouselDetailsAccessibility,
    carouselCardAccessibility
  } = _railADAmeta || {};
  const {
    countLabel,
    previousAccessibility,
    nextAccessibility
  } = _railMeta || {};
  const a11yProps = {
    prevSlideMessage: previousAccessibility,
    nextSlideMessage: nextAccessibility
  };
  if (carouselCardAccessibility) {
    a11yProps.slideLabelMessage = carouselCardAccessibility;
  }

  /**
   * Swiper count and navigation references
   */
  const [currentIndex, updateCurrentIndex] = (0, _react.useState)(1);
  const [swiper, setSwiper] = (0, _react.useState)(null); // eslint-disable-line
  const [isInit, setIsInit] = (0, _react.useState)(true);
  const [newSwiperCounter, setNewSwiperCounter] = (0, _react.useState)(id || null);

  // Generate a per-instance fallback key once (doesn't re-run on re-render)
  const fallbackKeyRef = (0, _react.useRef)((0, _nanoid.nanoid)());

  /**
   * This useEffect sets the newSwiperCounter when an id is given
   */
  (0, _react.useEffect)(() => {
    // return if id is given from CMS
    if (id) {
      return;
    }
    const isBrowser = typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';
    if (!isBrowser) {
      return;
    }

    // Scope by unique instance ID
    const storageKey = `RailSwiperFallbackId-${fallbackKeyRef.current}`;
    let storedId = sessionStorage.getItem(storageKey);
    if (!storedId) {
      storedId = (0, _nanoid.nanoid)();
      sessionStorage.setItem(storageKey, storedId);
    }
    setNewSwiperCounter(storedId);
  }, [id]);

  // Don't render Swiper until we have a stable ID
  if (!newSwiperCounter) {
    return null;
  }
  const swiperInit = instance => {
    setSwiper(instance);
    setIsInit(false);
  };
  const isMobile = currentBreakpoint === 'SM';
  const isSingleItem = items?.length === 1;
  const haveMobilePeek = mobilePeek && !isSingleItem;
  const haveTabletPeek = tabletPeek && !isSingleItem;
  const isLastItem = currentIndex === items?.length;
  const mobileHighlighter = isHighlighter && isMobile;

  /* Gets all of the slidesPerView and slidesPerGroup for each breakpoint */
  const setSlidesPerViewXL = (0, _carouselUtils.getSlidesPerViewXL)({
    isSingleItem: isSingleItem,
    slidesPerViewAuto: slidesPerViewAuto,
    sliderPerViewXL: sliderPerViewXL
  });
  const setSlidesPerGroupXL = (0, _carouselUtils.getSlidesPerGroupXL)({
    slidesPerGroupXL: slidesPerGroupXL
  });
  const setSlidesPerViewLG = (0, _carouselUtils.getSlidesPerViewLG)({
    isHighlighter: isHighlighter,
    sliderHighlightPerViewLG: sliderHighlightPerViewLG,
    isSingleItem: isSingleItem,
    slidesPerViewAuto: slidesPerViewAuto,
    sliderPerViewLG: sliderPerViewLG
  });
  const setSlidesPerGroupLG = (0, _carouselUtils.getSlidesPerGroupLG)({
    isHighlighter: isHighlighter,
    slidesHighlightPerGroupLG: slidesHighlightPerGroupLG,
    slidesPerGroupLG: slidesPerGroupLG
  });
  const setSlidesPerViewMD = (0, _carouselUtils.getSlidesPerViewMD)({
    isSingleItem: isSingleItem,
    slidesPerViewAuto: slidesPerViewAuto,
    sliderPerViewMD: sliderPerViewMD
  });
  const setSlidesPerGroupMD = (0, _carouselUtils.getSlidesPerGroupMD)({
    slidesPerGroupMD: slidesPerGroupMD
  });
  const setSlidesPerViewSM = (0, _carouselUtils.getSlidesPerViewSM)({
    isSingleItem: isSingleItem,
    slidesPerViewAuto: slidesPerViewAuto,
    sliderPerViewSM: sliderPerViewSM
  });
  const setSlidesPerGroupSM = (0, _carouselUtils.getSlidesPerGroupSM)({
    slidesPerGroupSM: slidesPerGroupSM
  });
  const componentClassName = (0, _classnames.default)('pal-c-Rail', {
    [`pal-c-Rail__bullets`]: showBullets,
    [`pal-c-Rail__init`]: isInit,
    [`pal-c-Rail__lastItem`]: isLastItem && !isSingleItem,
    [`pal-c-Rail__mobilePeek`]: haveMobilePeek,
    [`pal-c-Rail__singleItem`]: isSingleItem,
    [`pal-c-Rail__tabletPeek`]: haveTabletPeek
  });
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName
  }, headline && /*#__PURE__*/_react.default.createElement(_TitlesDefault.default, {
    countLabel: showCountLabel ? countLabel : '',
    headline: headline,
    headlineTag: headlineTag,
    externalAction: externalActionElementLink,
    subheadline: subheadline,
    textAlignment: "left"
  }), mobileHighlighter && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-Rail__highlightedProduct"
  }, externalElementMobileSpotlightCard && (0, _renderObject.renderObject)(externalElementMobileSpotlightCard)), /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-Rail__carousel",
    role: "region",
    "aria-label": carouselAccessibility
  }, carouselDetailsAccessibility && /*#__PURE__*/_react.default.createElement("p", {
    className: "sr-only"
  }, carouselDetailsAccessibility), /*#__PURE__*/_react.default.createElement("div", {
    className: `pal-c-Rail__carousel--pagination pal-c-Rail__carousel--pagination-${newSwiperCounter}`
  }, externalActionElementBackButton && (0, _renderObject.renderObject)(externalActionElementBackButton)), /*#__PURE__*/_react.default.createElement(_react2.Swiper
  // install Swiper modules
  , {
    modules: [_modules.A11y, _modules.FreeMode, _modules.Keyboard, _modules.Navigation, _modules.Pagination],
    ref: carouselRef,
    style: {
      '--swiper-pagination-bullet-horizontal-gap': '0.5rem',
      '--swiper-pagination-bullet-size': '0.75rem'
    },
    a11y: a11yProps,
    breakpoints: {
      [BREAKPOINTS.X_LARGE]: {
        slidesPerView: setSlidesPerViewXL,
        slidesPerGroup: setSlidesPerGroupXL
      },
      [BREAKPOINTS.LARGE]: {
        slidesPerView: setSlidesPerViewLG,
        slidesPerGroup: setSlidesPerGroupLG
      },
      [BREAKPOINTS.MEDIUM]: {
        slidesPerView: setSlidesPerViewMD,
        slidesPerGroup: setSlidesPerGroupMD
      },
      [BREAKPOINTS.SMALL]: {
        slidesPerView: setSlidesPerViewSM,
        slidesPerGroup: setSlidesPerGroupSM
      }
    },
    centeredSlides: centeredSlides,
    freeMode: {
      enabled: useFreeMode,
      minimumVelocity: 0.01,
      momentum: true,
      momentumBounce: true,
      momentumBounceRatio: 1.2,
      momentumRatio: 1.2,
      momentumVelocityRatio: 1.2,
      sticky: true
    },
    navigation: {
      prevEl: `.pal-c-Rail__carousel--pagination-${newSwiperCounter} .pal-c-Rail__carousel--arrowBack`,
      nextEl: `.pal-c-Rail__carousel--pagination-${newSwiperCounter} .pal-c-Rail__carousel--arrowForward`,
      disabledClass: 'pal-c-Rail__carousel--disabled'
    },
    observer: useObserver /* CarouselDesktop uses this */,
    observeSlideChildren: useObserveSlideChildren /* CarouselDesktop uses this */,
    onObserverUpdate: swiper => swiper.slideTo(0, 200, false) /* CarouselDesktop uses this */,
    onSwiper: swiper => {
      swiperInit();
      (0, _carouselUtils.handleInactiveSwiperSlides)(swiper);
    },
    onSlidesUpdated: swiper => {
      (0, _carouselUtils.handleInactiveSwiperSlides)(swiper);
    },
    onSlideChange: swiper => {
      updateCurrentIndex(swiper?.realIndex + 1);
      (0, _carouselUtils.handleInactiveSwiperSlides)(swiper);
      (0, _carouselUtils.handleSlideFocus)({
        swiper
      });
    },
    onSlidesLengthChange: swiper => {
      (0, _carouselUtils.handleInactiveSwiperSlides)(swiper);
    },
    pagination: {
      type: showBullets ? 'bullets' : '',
      clickable: 'true'
    },
    preloadImages: true,
    spaceBetween: spaceBetween /* PillCarousel uses this */,
    slidesOffsetAfter: slidesOffsetAfter /* Sets an offsest after the swiper send PX value */,
    slidesOffsetBefore: slidesOffsetBefore /* Sets an offsest after the swiper send PX value */,
    speed: 600,
    watchOverflow: true,
    watchSlidesProgress: true,
    watchSlidesVisibility: true
  }, children), /*#__PURE__*/_react.default.createElement("div", {
    className: `pal-c-Rail__carousel--pagination pal-c-Rail__carousel--pagination-${newSwiperCounter}`
  }, externalActionElementForwardButton && (0, _renderObject.renderObject)(externalActionElementForwardButton))));
};
exports.Rail = Rail;
const Slide = props => {
  const {
    children,
    className
  } = props;
  const componentClassName = (0, _classnames.default)('pal-c-Rail__slide', className);
  return /*#__PURE__*/_react.default.createElement(_react2.SwiperSlide, {
    className: componentClassName
  }, children);
};

/**
 * Property type definitions
 * @typedef RailProps
 * @type {object}
 * @property {any} carouselRef - Carousel reference
 * @property {boolean} centeredSlides - Sets the slides to be centered
 * @property {node} children - Children slot where components can be nested
 * @property {object} externalActionElementLink - external action object
 * @property {object} externalActionElementBackButton - external back button
 * @property {object} externalActionElementForwardButton = external forward button
 * @property {string} headline - Sets the headline title.
 * @property {string} headlineTag - Sets the headline title tag.
 * @property {string} id - Amplience component ID for the previous/next buttons
 * @property {boolean} isHighlighter - Boolean for the highlighter card
 * @property {array} items - Set the Item array.
 * @property {boolean} mobilePeek - Boolean for the right hand peek on mobile
 * @property {boolean} showBullets - Boolean for the pagination bullets
 * @property {boolean} showCountLabel - Boolean for the count label in the header
 * @property {number} slidesOffsetAfter - Sets an offsest in PX after the swiper
 * @property {number} slidesOffsetBefore - Sets an offsest in PX before the swiper
 * @property {number} sliderHighlightPerViewLG - Sets the sliders per view with highlight card on LG.
 * @property {number} sliderPerViewXL - Sets the sliders per view on XL.
 * @property {number} sliderPerViewLG - Sets the sliders per view on LG.
 * @property {number} sliderPerViewMD - Sets the sliders per view on MD.
 * @property {number} sliderPerViewSM - Sets the sliders per view on SM.
 * @property {boolean} slidesPerViewAuto - Sets the sliders per view to be auto
 * @property {number} slidesHighlightPerGroupLG - Sets the sliders per group with highlight card on LG.
 * @property {number} slidesPerGroupXL - Sets the sliders per group on XL.
 * @property {number} slidesPerGroupLG - Sets the sliders per group on LG.
 * @property {number} slidesPerGroupMD - Sets the sliders per group on MD.
 * @property {number} slidesPerGroupSM - Sets the sliders per group on SM.
 * @property {number} spaceBetween - Sets spacebetween number on the slides
 * @property {string} subheadline - Sets the subtitle.
 * @property {boolean} tabletPeek - Boolean for the right hand peek on tablet
 * @property {boolean} useFreeMode - Boolean whether the FreeMode is enabled
 * @property {boolean} useObserver - Boolean whether the useObserver is enabled
 * @property {boolean} useObserveSlideChildren - Boolean whether the useObserveSlideChildren is enabled
 * @property {object} _railADAmeta - Sets carouselAccessibility
 * @property {object} _railMeta - Sets nextAccessibility, previousAccessibility
 */
exports.Slide = Slide;
const propTypes = exports.propTypes = {
  /**
   * Carousel reference
   */
  carouselRef: _propTypes.default.any,
  /**
   * Sets the slides to be centered
   */
  centeredSlides: _propTypes.default.bool,
  /**
   * Children slot where components can be nested
   */
  children: _propTypes.default.node,
  /**
   * Sets the primary action from an external source that contains business logic
   */
  externalActionElementLink: _propTypes.default.object,
  /**
   * Sets the back action from an external source that contains business logic
   */
  externalActionElementBackButton: _propTypes.default.object,
  /**
   * Sets the forward action from an external source that contains business logic
   */
  externalActionElementForwardButton: _propTypes.default.object,
  /**
   * Sets the headline title.
   */
  headline: _propTypes.default.string,
  /**
   * Sets the headline title tag.
   */
  headlineTag: _propTypes.default.string,
  /**
   * Amplience component ID for the previous/next buttons
   */
  id: _propTypes.default.string,
  /**
   * Boolean for the highlighter card
   */
  isHighlighter: _propTypes.default.bool,
  /**
   * Set the Item array.
   */
  items: _propTypes.default.array,
  /**
   * Boolean for the right hand peek on mobile
   */
  mobilePeek: _propTypes.default.bool,
  /**
   * Boolean for the pagination bullets
   */
  showBullets: _propTypes.default.bool,
  /**
   * Boolean for the count label in the header
   */
  showCountLabel: _propTypes.default.bool,
  /**
   * Sets an offsest in PX after the swiper
   */
  slidesOffsetAfter: _propTypes.default.number,
  /**
   * Sets an offsest in PX before the swiper
   */
  slidesOffsetBefore: _propTypes.default.number,
  /**
   * Sets the sliders per view with highlight card on LG.
   */
  sliderHighlightPerViewLG: _propTypes.default.number,
  /**
   * Sets the sliders per view on XL.
   */
  sliderPerViewXL: _propTypes.default.number,
  /**
   * Sets the sliders per view on LG.
   */
  sliderPerViewLG: _propTypes.default.number,
  /**
   * Sets the sliders per view on MD.
   */
  sliderPerViewMD: _propTypes.default.number,
  /**
   * Sets the sliders per view on SM.
   */
  sliderPerViewSM: _propTypes.default.number,
  /**
   * Sets the sliders per group with highlight card on LG.
   */
  slidesHighlightPerGroupLG: _propTypes.default.number,
  /**
   * Sets the sliders per group on XL.
   */
  slidesPerGroupXL: _propTypes.default.number,
  /**
   * Sets the sliders per group on LG.
   */
  slidesPerGroupLG: _propTypes.default.number,
  /**
   * Sets the sliders per group on MD.
   */
  slidesPerGroupMD: _propTypes.default.number,
  /**
   * Sets the sliders per group on SM.
   */
  slidesPerGroupSM: _propTypes.default.number,
  /**
   * Sets the sliders per view to be auto
   */
  slidesPerViewAuto: _propTypes.default.bool,
  /**
   * Sets spacebetween number on the slides
   */
  spaceBetween: _propTypes.default.number,
  /**
   * Sets the subtitle.
   */
  subheadline: _propTypes.default.string,
  /**
   * Boolean for the right hand peek on tablet
   */
  tabletPeek: _propTypes.default.bool,
  /**
   * Boolean whether the FreeMode is enabled
   */
  useFreeMode: _propTypes.default.bool,
  /**
   * Boolean whether the useObserver is enabled
   */
  useObserver: _propTypes.default.bool,
  /**
   * Boolean whether the useObserveSlideChildren is enabled
   */
  useObserveSlideChildren: _propTypes.default.bool,
  /**
   * Sets carouselAccessibility
   */
  _railADAmeta: _propTypes.default.object,
  /**
   * Sets nextAccessibility, previousAccessibility
   */
  _railMeta: _propTypes.default.object
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {boolean} centeredSlides = 'false' - Sets the slides to be centered
 * @property {boolean} mobilePeek='true' - Boolean for the right hand peek on mobile
 * @property {boolean} showCountLabel='false' - Boolean for the count label in the header
 * @property {boolean} showBullets='false' - Boolean for the pagination bullets
 * @property {boolean} slidesPerViewAuto='false' - Sets the sliders per view to be auto
 * @property {number} spaceBetween='16' - Sets spacebetween number on the slides
 * @property {boolean} tabletPeek='false' - Boolean for the right hand peek on tablet
 * @property {boolean} useFreeMode='true' - Boolean whether the FreeMode is enabled
 * @property {boolean} useObserver='false' - Boolean whether the useObserver is enabled
 * @property {boolean} useObserveSlideChildren='false' - Boolean whether the useObserveSlideChildren is enabled
 */
const defaultProps = exports.defaultProps = {
  centeredSlides: false,
  mobilePeek: true,
  showBullets: false,
  showCountLabel: false,
  slidesPerViewAuto: false,
  spaceBetween: 16,
  tabletPeek: false,
  useFreeMode: true,
  useObserver: false,
  useObserveSlideChildren: false
};
Rail.propTypes = propTypes;
var _default = exports.default = Rail;